import { cn } from '@/utils/utils';
import { EMaterialStatus } from '@shelflix/schemas/material/enums';
import { Card } from '@shelflix/ui-web/components/ui/card';

type StatusCount = {
  status: EMaterialStatus;
  count: number;
};

type MaterialCountBadgeProps = {
  statusCounts: StatusCount[];
  className?: string;
  isEmpty?: boolean;
};

const getStatusBadgeColors = (status: EMaterialStatus) => {
  switch (status) {
    case EMaterialStatus.ACTIVE:
      return { bg: 'bg-status-active', text: 'text-white' };
    case EMaterialStatus.OPERATIONALONLINE:
      return { bg: 'bg-status-active', text: 'text-white' };
    case EMaterialStatus.OFFLINE:
      return { bg: 'bg-status-offline', text: 'text-white' };
    case EMaterialStatus.MAINTENANCE:
      return { bg: 'bg-status-maintenance', text: 'text-white' };
    case EMaterialStatus.STANDBY:
      return { bg: 'bg-status-maintenance', text: 'text-white' };
    default:
      return { bg: 'bg-gray-500', text: 'text-white' };
  }
};

/**
 * MaterialCountBadge - Displays material count badges by status
 *
 * Used on mapbox pins and pin groups to show the count of materials
 * grouped by their status (ACTIVE, OFFLINE, MAINTENANCE, DISCARDED).
 *
 * Colors are defined in @shelflix/ui-web/src/styles.css:
 * - ACTIVE: Green
 * - OFFLINE: Red
 * - MAINTENANCE: Orange
 * - DISCARDED: Purple
 *
 * @param statusCounts - Array of status counts to display
 * @param className - Optional additional classes for the container
 * @param isEmpty - Whether to show empty state (gray "0" badge)
 */
export function MaterialCountBadge({ statusCounts, className, isEmpty = false }: MaterialCountBadgeProps) {
  // Show status-specific badges
  if (statusCounts.length > 0) {
    return (
      <div className={cn('absolute -top-2 -right-2 z-10 flex flex-row gap-[2px]', className)}>
        {statusCounts.map(({ status, count }) => {
          const colors = getStatusBadgeColors(status);
          return (
            <Card
              key={status}
              className={cn(
                'rounded-[6px] px-1 py-0 text-[10px] font-bold',
                colors.bg,
                colors.text,
                'border border-solid border-white/20',
                'min-w-[16px] text-center'
              )}
            >
              {count}
            </Card>
          );
        })}
      </div>
    );
  }

  // Show empty state (only if explicitly marked as empty)
  if (isEmpty) {
    return (
      <Card
        className={cn(
          'absolute -top-2 -right-2 z-10 rounded-[6px] px-1 py-0 text-[10px] font-bold text-black',
          'bg-gray-300',
          'border border-solid border-[var(--border-color)]',
          'min-w-[16px] text-center',
          className
        )}
      >
        0
      </Card>
    );
  }

  // Don't render anything if no counts and not empty
  return null;
}
